import { useState } from 'react';
import { IMaskInput } from 'react-imask';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { Field, FieldLabel, FormField, FormModal, SearchSelect, Select, TextArea } from '../../components';
import { useCreateClient, useUpdateClient } from '../../queries/clients';
import { useProperties } from '../../queries/properties';
import { assigneeName, useBookingAssigneesQuery } from '../../queries/scheduling';
import { formatPhone } from '../../utils/callLog';
import { STATUSES, statusOf } from './constants';
import { SavedNote } from './styled';

const toForm = (c = {}) => ({
  name: c.name || '',
  tenantStatus: statusOf(c),
  phone: c.phone ? formatPhone(c.phone) : '',
  email: c.email || '',
  listingId: c.unit?._id || '',
  lease: c.lease || '',
  assignedTo: c.assignedTo || '',
  note: ''
});

function useAgentOptions(current) {
  const { data: assignees = [] } = useBookingAssigneesQuery();
  return [...new Set([...assignees.map(assigneeName), current].filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

export default function TenantFormModal({ tenant, onClose }) {
  const editing = Boolean(tenant?._id);
  const [form, setForm] = useState(() => toForm(tenant));
  const [saved, setSaved] = useState('');
  const create = useCreateClient();
  const update = useUpdateClient();
  const { rows: units } = useProperties();
  const agents = useAgentOptions(tenant?.assignedTo);
  const valid = Boolean(form.name.trim());

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const pick = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const save = () => {
    const { note, ...values } = form;
    const payload = { ...values, name: values.name.trim(), listingId: values.listingId || null };
    if (editing) {
      update.mutate({ _id: tenant._id, ...payload });
      onClose();
      return;
    }
    create.mutate(
      { ...payload, note },
      {
        onSuccess: (data) => {
          setSaved(data.client.name);
          setForm(toForm());
        }
      }
    );
  };

  return (
    <FormModal
      title={editing ? `Edit ${tenant.name}` : 'Add tenant record'}
      subtitle={editing ? 'Update this tenant record' : 'New tenant or prospect'}
      okText={editing ? 'Save changes' : 'Save tenant'}
      valid={valid}
      loading={create.isPending}
      onSubmit={save}
      onClose={onClose}
    >
      {saved && (
        <SavedNote role="status">
          <FontAwesomeIcon icon={faCircleCheck} />
          {saved} added to Tenants.
        </SavedNote>
      )}
      <FormField>
        <FieldLabel htmlFor="tenant-name">Full name</FieldLabel>
        <Field id="tenant-name" required autoFocus placeholder="First and last name" value={form.name} onChange={set('name')} />
      </FormField>
      <FormField>
        <FieldLabel htmlFor="tenant-status">Record status</FieldLabel>
        <Select id="tenant-status" value={form.tenantStatus} onChange={set('tenantStatus')}>
          {STATUSES.map((s) => (
            <option key={s.key} value={s.key}>
              {s.key}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField>
        <FieldLabel htmlFor="tenant-phone">Phone</FieldLabel>
        <Field
          as={IMaskInput}
          id="tenant-phone"
          type="tel"
          mask="(000) 000-0000"
          placeholder="(555) 555-0100"
          value={form.phone}
          onAccept={(phone) => setForm((f) => ({ ...f, phone }))}
        />
      </FormField>
      <FormField>
        <FieldLabel htmlFor="tenant-email">Email</FieldLabel>
        <Field id="tenant-email" type="email" placeholder="name@example.com" value={form.email} onChange={set('email')} />
      </FormField>
      <FormField>
        <FieldLabel htmlFor="tenant-unit">Property / unit</FieldLabel>
        <SearchSelect
          id="tenant-unit"
          allowClear
          placeholder="No unit"
          value={form.listingId}
          onChange={pick('listingId')}
          options={units.map((u) => {
            const taken = Boolean(u.tenantId && u.tenantId !== tenant?._id);
            return { value: u._id, label: taken ? `${u.name} (occupied)` : u.name, disabled: taken };
          })}
        />
      </FormField>
      <FormField>
        <FieldLabel htmlFor="tenant-lease">Lease</FieldLabel>
        <Field id="tenant-lease" placeholder="In effect through Feb 2027" value={form.lease} onChange={set('lease')} />
      </FormField>
      <FormField>
        <FieldLabel htmlFor="tenant-agent">Assigned agent</FieldLabel>
        <SearchSelect
          id="tenant-agent"
          allowClear
          placeholder="Unassigned"
          value={form.assignedTo}
          onChange={pick('assignedTo')}
          options={agents.map((a) => ({ value: a, label: a }))}
        />
      </FormField>
      {!editing && (
        <FormField>
          <FieldLabel htmlFor="tenant-notes">Notes</FieldLabel>
          <TextArea
            id="tenant-notes"
            placeholder="Anything the next person picking this up should know"
            value={form.note}
            onChange={set('note')}
          />
        </FormField>
      )}
    </FormModal>
  );
}
